import { effect, inject, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ServiceWorkerModule } from '@angular/service-worker';
import { RouteReuseStrategy } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app.component';
import { APP_CONFIG, appConfig } from './app.config';
import { KonfliktHinweisComponent, SitzungsHinweisComponent, WdDevWarningBannerComponent, WdNavbarHeightDirective, WdConnectionStatusComponent, WdGpsStatusComponent, WdCompassRoseComponent, WdStorageStatusComponent, WdErrorBadgeComponent } from '@luzula/luzula-lib';
import { AppRoutingModule } from './app-routing.module';
import { NavbarComponent } from './navbar/navbar.component';
import { IconsModule } from './icons/icons.module';

// App-Komponenten
import { AlleBohrpunkteComponent } from './alle-bohrpunkte/alle-bohrpunkte.component';
import { AuthButtonComponent } from './navbar/auth-button/auth-button.component';
import { ListenverwaltungComponent } from './listenverwaltung/listenverwaltung.component';
import { GenericListEditorComponent } from './listenverwaltung/generic-list-editor.component';
import { PunktartenEditorComponent } from './listenverwaltung/punktarten-editor.component';
import { BodensteckbriefPreviewComponent } from './listenverwaltung/bodensteckbrief-preview.component';
import { BodensteckbriefEditorComponent } from './listenverwaltung/bodensteckbrief-editor.component';
import { SeaOriginalComponent } from './listenverwaltung/sea-original.component';
import { BodenformenkatalogReaderComponent } from './listenverwaltung/bodenformenkatalog-reader.component';
import { SteckbriefFensterComponent } from './listenverwaltung/steckbrief-fenster.component';
import { NamensmatrixModalComponent } from './listenverwaltung/namensmatrix-modal.component';
import { ChangelogComponent } from './changelog/changelog.component';
import { PwaInstallBannerComponent } from '@luzula/luzula-lib';
import { PresenceBadgeComponent } from './presence/presence-badge.component';
import { AbschnittWahlComponent } from './bohrpunkte/uebernahme/abschnitt-wahl.component';

// Bohrpunkte-Komponenten (Barrel-Import)
import {
	BohrpunktComponent,
	BohrpunktTabsComponent,
	BohrpunktNeuComponent,
	RotationPickerComponent,
	PunktartPickerComponent,
	ReliefPickerComponent,
	BohrpunktNummerComponent,
	BohrpunktPlausiComponent,
	BohrpunktPrintViewComponent,
	MultiPrintViewComponent,
	BohrpunktAllgemeinComponent,
	BohrpunktBodenComponent,
	BohrpunktKurzprotokollComponent,
	BohrpunktVegetationComponent,
	BohrpunktWeitereComponent,
	BohrpunktMapComponent,
	GrubenbildComponent,
	HlAreaDirective,
	HlAreaComponent,
	HlAreaContainerComponent,
	AdresseComponent,
	AufnahmeComponent,
	BodenComponent,
	KurzprotokollComponent,
	NaturraumComponent,
	SubstrateHorizonteComponent,
	BaumartenComponent,
	BestandComponent,
	VegetationsaufnahmeComponent,
	BohrpunktListComponent,
	BohrpunktListPanelComponent,
	ErrorComponent,
	SandboxControlsComponent,
} from './bohrpunkte';

// Controls
import { LuzulaControlsModule, MapModule, MapStatusService, StorageService, AvatarComponent, WorkflowHistoryComponent } from '@luzula/luzula-lib';
import { HelpModule } from './bohrpunkte/help/help.module';

// Services
import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { FilterService } from './filter.service';
import { EvaluationService } from './bohrpunkte/evaluation.service';
import { OptionService } from './bohrpunkte/option.service';
import { ValidationService } from './bohrpunkte/validation.service';
import { BestandConverterComponent } from './bohrpunkte/bestand/bestand-converter.component';
import { GpsService, ViewportService, MdbService, UpdateService, WdMapCacheRouteReuseStrategy, GlobalTileSeedService, MBTilesService, SplashScreenService, OfflineModeService, provideLuzulaInit, starteBasiskarte } from '@luzula/luzula-lib';
import { ProjectService } from './project.service';

// Keycloak
import { provideKeycloakAngular } from './keycloak.config';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { errorCaptureInterceptor, reauthInterceptor, ConsoleErrorService } from '@luzula/luzula-lib';


// App-Service Provider (provideLuzulaApp)
import { APP_PROVIDERS } from './core/app-providers';

// Pipes
import { MaskForGuestPipe } from '@luzula/luzula-lib';

// Aufnahme nach der Bodenkundlichen Kartieranleitung (KA): Lese-Reiter und die
// vier Eingabemasken. Nicht "standalone", weil sie `bpHl` und die Eingabefelder
// dieses Moduls benutzen.
import { KaUebersichtComponent } from './bohrpunkte/verfahren/ka/ka-uebersicht.component';
import { KaMaskeComponent } from './bohrpunkte/verfahren/ka/ka-maske.component';
import { KaTitelComponent } from './bohrpunkte/verfahren/ka/ka-titel.component';
import { KaSituationComponent } from './bohrpunkte/verfahren/ka/ka-situation.component';
import { KaHorizonteComponent } from './bohrpunkte/verfahren/ka/ka-horizonte.component';
import { KaProfilComponent } from './bohrpunkte/verfahren/ka/ka-profil.component';
// Spiegeltiefe unter oder über Flur: Feld mit Knopf und Anzeige (flur-tiefe.ts).
import { FlurTiefeInputComponent } from './bohrpunkte/detail/hl-area/flur-tiefe-input.component';
import { FlurTiefePipe } from './bohrpunkte/flur-tiefe.pipe';
@NgModule({
	bootstrap: [AppComponent],
	declarations: [
		// App-Komponenten
		AppComponent,
		AlleBohrpunkteComponent,
		AuthButtonComponent,
		NavbarComponent,
		PresenceBadgeComponent,
		ListenverwaltungComponent,
		GenericListEditorComponent,
		PunktartenEditorComponent,
		BodensteckbriefPreviewComponent,
		BodensteckbriefEditorComponent,
		SeaOriginalComponent,
		BodenformenkatalogReaderComponent,
		SteckbriefFensterComponent,
		NamensmatrixModalComponent,
		ChangelogComponent,

		// Bohrpunkte-Komponenten (ehemals BohrpunkteModule)
		BohrpunktComponent,
		BohrpunktPlausiComponent,
		BohrpunktPrintViewComponent,
		MultiPrintViewComponent,
		BohrpunktTabsComponent,
		BohrpunktListComponent,
		BohrpunktNummerComponent,
		BohrpunktKurzprotokollComponent,
		BohrpunktAllgemeinComponent,
		BohrpunktVegetationComponent,
		BohrpunktBodenComponent,
		BohrpunktWeitereComponent,
		BohrpunktMapComponent,
		BohrpunktNeuComponent,
		GrubenbildComponent,

		// HL-Area-Komponenten
		HlAreaDirective,
		HlAreaComponent,
		HlAreaContainerComponent,
		AdresseComponent,
		AufnahmeComponent,
		BodenComponent,
		KurzprotokollComponent,
		NaturraumComponent,
		SubstrateHorizonteComponent,
		BaumartenComponent,
		BestandComponent,
		BestandConverterComponent,
		VegetationsaufnahmeComponent,

		// Aufnahme nach der Kartieranleitung
		KaUebersichtComponent,
		KaMaskeComponent,
		KaTitelComponent,
		KaSituationComponent,
		KaHorizonteComponent,
		KaProfilComponent,

		// Weitere Bohrpunkte-Komponenten
		BohrpunktListPanelComponent,
		ErrorComponent,
		SandboxControlsComponent
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		KonfliktHinweisComponent,
		SitzungsHinweisComponent,
		CommonModule,
		FormsModule,
		TextFieldModule,
		IconsModule,
		MapModule,
		LuzulaControlsModule,
		RotationPickerComponent,
		PunktartPickerComponent,
		AbschnittWahlComponent,
		ReliefPickerComponent,
		AvatarComponent,
		WorkflowHistoryComponent,
		HelpModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			// SW immer an, AUSSER beim lokalen ng-serve-Loop auf localhost
			// (HMR + SW-Cache verursachen redundante Worker, pending chunks).
			// Lokal kann via localStorage.setItem('bp-dev-sw', '1') doch aktiviert werden.
			enabled: (typeof location === 'undefined'
				|| (location.hostname !== 'localhost' && location.hostname !== '127.0.0.1'))
				|| (typeof localStorage !== 'undefined' && localStorage.getItem('bp-dev-sw') === '1'),
			registrationStrategy: 'registerImmediately'
		}),
		ToastrModule.forRoot({
			timeOut: 3000,
			positionClass: 'toast-bottom-right',
			preventDuplicates: true,
			progressBar: true
		}),
		NgbModule,
		NgbTooltipModule,
		WdDevWarningBannerComponent,
		WdNavbarHeightDirective,
		WdConnectionStatusComponent,
		WdGpsStatusComponent,
		WdCompassRoseComponent,
		WdStorageStatusComponent,
		WdErrorBadgeComponent,
		MaskForGuestPipe,
		PwaInstallBannerComponent,
		FlurTiefeInputComponent,
		FlurTiefePipe
	],
	providers: [
		{ provide: APP_CONFIG, useValue: appConfig },
		provideKeycloakAngular(),
		provideLuzulaInit({
			steps: [
				{
					label: 'Basiskarte anstossen...',
					setup: () => {
						const mbtiles = inject(MBTilesService);
						const splash = inject(SplashScreenService);
						const oms = inject(OfflineModeService);
						const ces = inject(ConsoleErrorService);
						// Fortschritt zeigen, SOLANGE der Splash noch steht. Danach laufen
						// die Aufrufe ins Leere (das Element ist entfernt) - unschaedlich.
						const stop = effect(() => {
							const p = mbtiles.progress();
							if (mbtiles.loading()) {
								splash.updateProgress('Basiskarte wird geladen', p.loaded, p.total);
							}
						});
						return async () => {
							// Online (Normalfall): die grosse Offline-Basiskarte (osm-base.mbtiles)
							// NICHT vorab in den Speicher laden. Das blockierte den App-Start ~1 Min
							// (komplette mbtiles -> sql.js) und liess in dieser Zeit den Keycloak-
							// Auth-Code ablaufen, sodass die Anmeldung scheiterte. Online holt der
							// Karten-Layer die Basiskacheln on-demand von osm.luzula.de und cacht
							// nur das tatsaechlich Betrachtete. Volle mbtiles nur im Offline-Modus.
							//
							// Und im Offline-Modus wird nicht mehr GEWARTET (09.09.2026): Am Geraet
							// gemessen vergingen 86 bis 181 Sekunden bis zum Start, fast alles fuer
							// diese eine Datei - dazwischen ein weisser Bildschirm. Die Karte kommt
							// jetzt im Hintergrund nach und zeichnet sich selbst nach, sobald sie da
							// ist. Zeitgrenze und Fehlerfang stecken in `starteBasiskarte`.
							starteBasiskarte(
								mbtiles,
								oms.userOfflineMode(),
								// Der Meldehaken war da und zeigte ins Leere: Ein `console.warn`
								// erreicht den Fehler-Trichter nicht. Ausgerechnet dieser Schritt
								// laeuft NUR im Offline-Modus — also dort, wo niemand nachsehen kann.
								(text, err) => { console.warn(text, err); ces.melde(`[Basiskarte] ${text}`, err); },
								() => { stop.destroy(); splash.clearProgress(); },
							);
						};
					},
				},
			],
			eagerServices: [
				UpdateService, GpsService, AuthService, DataService, ProjectService,
				EvaluationService, FilterService, OptionService, ValidationService,
				MapStatusService, MdbService, StorageService, ViewportService,
				GlobalTileSeedService,
			],
		}),
		// Beide Interceptor-Quellen in einem Aufruf — sonst ueberschreibt das
		// zweite provideHttpClient die Konfig des ersten (letzter Aufruf gewinnt),
		// und der Bearer-Interceptor verschwindet stillschweigend.
		// reauthInterceptor MUSS zuerst stehen: bei 401 wiederholt er den Request,
		// wodurch der nachgelagerte Bearer-Interceptor den frischen Token anhaengt.
		provideHttpClient(
			withInterceptors([reauthInterceptor, includeBearerTokenInterceptor, errorCaptureInterceptor]),
			withInterceptorsFromDi(),
		),
		// Angular-20-Bootstrap-Fix (NG0203): Der Router loest seine RouteReuseStrategy
		// waehrend des langen async App-Initializers (keycloak.init) auf — dabei teils
		// AUSSERHALB des Injection-Contexts, wodurch der Konstruktor-@Inject(ROUTE_CACHE_TARGETS)
		// NG0203 warf und der App-Start abbrach (verdeckt gewesen vom frueheren Icons-NG0203).
		// Die Strategie hat keine echten DI-Abhaengigkeiten — nur die Ziel-Routes. Darum bauen
		// wir sie eager als useValue: das ist injektions-frei und damit context-unabhaengig.
		// Ziel-Routes bewusst gespiegelt aus ROUTE_CACHE_TARGETS (core/app-providers.ts:134).
		{ provide: RouteReuseStrategy, useValue: new WdMapCacheRouteReuseStrategy(['handbuch', 'listenverwaltung']) },
		...APP_PROVIDERS
	]
})
export class AppModule { }
